import * as THREE from 'three';
import React, {
  Suspense,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import { OrbitControls, useGLTF } from '@react-three/drei';
import { Canvas, useLoader } from '@react-three/fiber';
import styled from 'styled-components';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { TextureLoader } from 'three';
import Row from 'react-bootstrap/Row';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import { useNavigate } from 'react-router-dom';
import { Store } from '../Store';
import axios from 'axios';
import { getError } from '../utils';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import Rating from '../components/Rating';
import Badge from 'react-bootstrap/Badge';
import MessageBox from '../components/MessageBox';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import LoadingBox from '../components/LoadingBox';
import { Link } from 'react-router-dom';
import { HexColorPicker } from 'react-colorful';
import { proxy, useSnapshot } from 'valtio';
import LoaderTHREE from '../components/LoaderTHREE';

const colorState = proxy({
  items: {
    hands: '#FFDB00'
  }
});

const CanvasContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const ButtonsContainer = styled.div`
  position: relative;
  width: 200px;
  height: 145px;
`;

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, product: action.payload, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'CREATE_REVIEW_REQUEST':
      return { ...state, loadingCreatingReview: true };
    case 'CREATE_REVIEW_SUCCESS':
      return { ...state, loadingCreatingReview: false };
    case 'CREATE_REVIEW_FAIL':
      return { ...state, loadingCreatingReview: false };
    case 'REFRESH_PRODUCT':
      return { ...state, product: action.payload };
    default:
      return state;
  }
};

function Picker() {
  const snap = useSnapshot(colorState);
  return (
    <div>
      <HexColorPicker
        className="picker"
        color={snap.items.hands}
        onChange={(color) => (colorState.items.hands = color)}
      />
    </div>
  );
}

export default function MinifigureScreen() {
  let reviewsRef = useRef('');
  const snap = useSnapshot(colorState);

  const [showHat, setShowHat] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [headExtra, setHeadExtra] = useState(0);
  const [bodyExtra, setBodyExtra] = useState(0);
  const [legsExtra, setLegsExtra] = useState(0);

  const [selectedImage, setSelectedImage] = useState('');
  const [haveCreatedReview, setHaveCreatedReview] = useState(false);
  const navigate = useNavigate();

  const slug = 'create-your-own-minifigure';
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { cart, userInfo } = state;

  const [{ loading, error, product, loadingCreatingReview }, dispatch] =
    useReducer(reducer, {
      product: [],
      loading: true,
      error: ''
    });

  useEffect(() => {
    const featchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const result = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/products/slug/${slug}`
        );
        dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
        setSelectedImage(result.data.image);
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };

    featchData();
  }, [slug]);

  useEffect(() => {
    if (userInfo && product && product.reviews && product.reviews.length > 0) {
      const userReview = product.reviews.find(
        (review) => review.name === userInfo.name
      );
      if (userReview) {
        setHaveCreatedReview(true);
      }
    }
  }, [userInfo, product]);

  const addToCartHandler = async () => {
    const existItem = cart.cartItems.find((x) => x._id === product._id);
    const quantity = existItem ? existItem.quantity + 1 : 1;
    const { data } = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/products/${product._id}`
    );
    if (data.countInStock < quantity) {
      window.alert('Sorry, Product is Unavalable at the moment');
      return;
    }
    ctxDispatch({
      type: 'CART_ADD_ITEM',
      payload: {
        ...product,
        quantity,
        price: product.price + headExtra + bodyExtra + legsExtra
      }
    });
    navigate('/cart');
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!comment || !rating) {
      toast.error('Please enter comment and rating');
      return;
    }
    try {
      dispatch({ type: 'CREATE_REVIEW_REQUEST' });
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/products/${product._id}/reviews`,
        {
          rating,
          comment,
          name: userInfo.name
        },
        {
          headers: { authorization: `Bearer ${userInfo.token}` }
        }
      );

      dispatch({ type: 'CREATE_REVIEW_SUCCESS' });
      toast.success('Review submitted successfully');
      product.reviews.unshift(data.review);
      product.numReviews = data.rating;
      dispatch({ type: 'REFRESH_PRODUCT', payload: product });
      window.scrollTo({
        behavior: 'smooth',
        top: reviewsRef.current.offsetTop
      });
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'CREATE_REVIEW_FAIL' });
    }
  };

  const { nodes } = useLoader(GLTFLoader, '/legoModel.glb', (loader) => {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/draco/');
    dracoLoader.setDecoderConfig({ type: 'js' });
    loader.setDRACOLoader(dracoLoader);
  });

  const [
    torsoTexture,
    faceTexture,
    waisteTexture,
    leftFootTexture,
    rightFootTexture,
    spiderManFaceTexture,
    venomFaceTexture,
    captenFaceTexture,
    face2Texture,
    spiderTorsoTexture,
    spider2TorsoTexture,
    venomTorsoTexture,
    pants1texturew,
    pants1texturel,
    pants1texturer,
    pants2texturew,
    pants2texturel,
    pants2texturer,
    pants3texturew,
    pants3texturel,
    pants3texturer,
    pants4texturew,
    pants4texturel,
    pants4texturer
  ] = useLoader(TextureLoader, [
    '/images/textures/torso.jpg',
    '/images/textures/face.png',
    '/images/textures/waist.png',
    '/images/textures/leftFoot.png',
    '/images/textures/rightFoot.png',
    '/images/textures/spider.png',
    '/images/textures/venom.png',
    '/images/textures/capten.png',
    '/images/textures/face-2.png',
    '/images/textures/spiderTorso.png',
    '/images/textures/spider2Torso.png',
    '/images/textures/venomTorso.png',
    '/images/textures/pants-1-w.png',
    '/images/textures/pants-1-l.png',
    '/images/textures/pants-1-r.png',
    '/images/textures/pants-2-w.png',
    '/images/textures/pants-2-l.png',
    '/images/textures/pants-2-r.png',
    '/images/textures/pants-3-w.png',
    '/images/textures/pants-3-l.png',
    '/images/textures/pants-3-r.png',
    '/images/textures/pants-4-w.png',
    '/images/textures/pants-4-l.png',
    '/images/textures/pants-4-r.png'
  ]);

  const textures = [
    torsoTexture,
    faceTexture,
    waisteTexture,
    leftFootTexture,
    rightFootTexture,
    spiderManFaceTexture,
    venomFaceTexture,
    captenFaceTexture,
    face2Texture,
    spiderTorsoTexture,
    spider2TorsoTexture,
    venomTorsoTexture,
    pants1texturew,
    pants1texturel,
    pants1texturer,
    pants2texturew,
    pants2texturel,
    pants2texturer,
    pants3texturew,
    pants3texturel,
    pants3texturer,
    pants4texturew,
    pants4texturel,
    pants4texturer
  ];
  textures.map((texture) => {
    texture.flipY = false;
    texture.encoding = THREE.sRGBEncoding;
    texture.needsUpdate = true;
    return texture;
  });

  const headArray = [
    {
      texture: faceTexture,
      src: '/images/textures/face.png',
      price: 0
    },
    {
      texture: spiderManFaceTexture,
      src: '/images/textures/spider.png',
      price: 4
    },
    {
      texture: venomFaceTexture,
      src: '/images/textures/venom.png',
      price: 4
    },
    {
      texture: captenFaceTexture,
      src: '/images/textures/capten.png',
      price: 4
    },
    {
      texture: face2Texture,
      src: '/images/textures/face-2.png',
      price: 2
    }
  ];

  const handsArray = [
    {
      price: 1
    }
  ];

  const bodyArray = [
    {
      texture: spiderTorsoTexture,
      src: '/images/textures/spiderTorso.png',
      price: 4
    },
    {
      texture: spider2TorsoTexture,
      src: '/images/textures/spider2Torso.png',
      price: 4
    },
    {
      texture: venomTorsoTexture,
      src: '/images/textures/venomTorso.png',
      price: 4
    },
    {
      texture: torsoTexture,
      src: '/images/textures/torso.jpg',
      price: 0
    }
  ];

  const legsArray = [
    {
      texture: {
        waist: pants1texturew,
        left: pants1texturel,
        right: pants1texturer
      },
      src: '/images/pants-1.jpg',
      price: 4
    },
    {
      texture: {
        waist: pants2texturew,
        left: pants2texturel,
        right: pants2texturer
      },
      src: '/images/pants-2.jpg',
      price: 3
    },
    {
      texture: {
        waist: pants3texturew,
        left: pants3texturel,
        right: pants3texturer
      },
      src: '/images/pants-3.jpg',
      price: 3
    },
    {
      texture: {
        waist: pants4texturew,
        left: pants4texturel,
        right: pants4texturer
      },
      src: '/images/pants-4.jpg',
      price: 3
    }
  ];

  const [headTexture, setHeadTexture] = useState(faceTexture);
  const [bodyTexture, setBodyTexture] = useState(torsoTexture);
  const [beltTexture, setBeltTexture] = useState(waisteTexture);
  const [leftLegTexture, setLeftLegTexture] = useState(leftFootTexture);
  const [rightLegTexture, setRightLegTexture] = useState(rightFootTexture);

  //   useEffect(() => {
  //     torsoMaterial.map = lego;
  //     torsoMaterial.needsUpdate = true;
  //   }, []);

  const torsoMaterial = new THREE.MeshStandardMaterial({
    map: bodyTexture,
    color: '#ffffff'
  });
  const waisteMaterial = new THREE.MeshStandardMaterial({
    map: beltTexture,
    color: '#ffffff'
  });
  const leftFootMaterial = new THREE.MeshStandardMaterial({
    map: leftLegTexture,
    color: '#ffffff'
  });
  const rightFootMaterial = new THREE.MeshStandardMaterial({
    map: rightLegTexture,
    color: '#ffffff'
  });
  const faceMaterial = new THREE.MeshStandardMaterial({
    map: headTexture,
    color: '#ffffff'
  });
  const handsMaterial = new THREE.MeshStandardMaterial({
    color: snap.items.hands
  });
  const hatMaterial = new THREE.MeshStandardMaterial({
    color: '#ff0000'
  });
  const hatAccessMaterial = new THREE.MeshStandardMaterial({
    color: '#000000'
  });

  const group = useRef();

  const cameraOpts = {
    fov: 45,
    near: 1,
    far: 100,
    position: [0, 0, 10]
  };

  const onCanvasCreated = ({ gl }) => {
    /* eslint no-param-reassign: "error" */
    // gl.shadowMap.enabled = true;
    // gl.shadowMap.type = THREE.PCFShadowMap;
    // gl.gammaOutput = true;
    // gl.gammaFactor = 2.2;
    // gl.toneMappingExposure = 1.0;
    /* eslint-env browser */
    gl.setPixelRatio(window.devicePixelRatio > 2 ? 2 : window.devicePixelRatio);
  };

  const [openedOptions, setOpenedOptions] = useState(false);
  const [arrayOfOptions, setArrayOfOptions] = useState({
    part: '',
    options: [
      {
        texture: '',
        src: ''
      }
    ]
  });

  function open(str) {
    switch (str) {
      case 'head':
        setOpenedOptions(true);
        setArrayOfOptions((prev) => {
          return { ...prev, part: 'head', options: headArray };
        });
        break;
      case 'body':
        setOpenedOptions(true);
        setArrayOfOptions((prev) => {
          return { ...prev, part: 'body', options: bodyArray };
        });
        break;
      case 'legs':
        setOpenedOptions(true);
        setArrayOfOptions((prev) => {
          return { ...prev, part: 'legs', options: legsArray };
        });
        break;
      case 'hands':
        setOpenedOptions(true);
        setArrayOfOptions((prev) => {
          return { ...prev, part: 'hands', options: handsArray };
        });
        break;
      default:
        return;
    }
  }

  const changeTexutre = (part, o) => {
    switch (part) {
      case 'head':
        setHeadTexture(o.texture);
        setHeadExtra(o.price);
        break;
      case 'body':
        setBodyTexture(o.texture);
        setBodyExtra(o.price);
        break;
      case 'legs':
        setBeltTexture(o.texture.waist);
        setLeftLegTexture(o.texture.left);
        setRightLegTexture(o.texture.right);
        setLegsExtra(o.price);
        break;
      default:
        return;
    }
  };

  function hoverColor(r) {
    r.style.filter =
      'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)';
  }
  function hoverUncolor(r) {
    r.style.filter = '';
  }
  console.log(selectedImage);

  return loading ? (
    <LoadingBox />
  ) : error ? (
    <MessageBox varient="danger">{error}</MessageBox>
  ) : (
    <Container>
      <div className="d-flex flex-wrap justify-content-center">
        <Col className="option" md={8} style={{ height: '600px' }}>
          <CanvasContainer>
            <Canvas
              camera={cameraOpts}
              //   pixelRatio={window.devicePixelRatio}
              onCreated={onCanvasCreated}
            >
              <pointLight
                visible
                intensity={1}
                debug
                color="white"
                position={[0, 200, 0]}
                rotation={[Math.PI / -2.5, 0, 0]}
              />
              <ambientLight color={'white'} intensity={0.5} />
              <directionalLight
                // castShadow
                position={[10, 20, 15]}
                // shadow-camera-right={8}
                // shadow-camera-top={8}
                // shadow-camera-left={-8}
                // shadow-camera-bottom={-8}
                // shadow-mapSize-width={1024}
                // shadow-mapSize-height={1024}
                intensity={0.5}
                // shadow-bias={-0.0001}
              />
              <OrbitControls enablePan={false} />
              <Suspense fallback={<LoaderTHREE />}>
                <group ref={group} position-y={-0.5} position-z={0}>
                  <mesh
                    name="Torso"
                    geometry={nodes.torso.geometry}
                    material={torsoMaterial}
                    // position={[0, -15, 0]}
                    // rotation={[Math.PI / 2, 0, 0]}
                  />
                  <mesh
                    name="Head"
                    geometry={nodes.head.geometry}
                    material={faceMaterial}
                    position={[0, 1.8, 0]}
                    // rotation={[Math.PI / 2, 0, 0]}
                    // {...extras}
                  />
                  <mesh
                    name="Hands"
                    geometry={nodes.hands.geometry}
                    material={handsMaterial}
                    position={[-0.87, 1.35, 0]}
                    rotation={[0, 0, 0]}
                  />
                  <mesh
                    name="waist"
                    geometry={nodes.waist.geometry}
                    material={waisteMaterial}
                    // position={[-0.87, 1.45, 0]}
                    // rotation={[0, 0, 0]}
                  />
                  <mesh
                    name="leftFoot"
                    geometry={nodes.leftFoot.geometry}
                    material={rightFootMaterial}
                    position={[-0.57, -0.5, 0]}
                    // rotation={[0, 0, 0]}
                  />
                  <mesh
                    name="rightFoot"
                    geometry={nodes.rightFoot.geometry}
                    material={leftFootMaterial}
                    position={[0.57, -0.5, 0]}
                    // rotation={[0, Math.PI, 0]}
                  />
                  <mesh
                    visible={showHat}
                    name="hat"
                    geometry={nodes.hat.geometry}
                    material={hatMaterial}
                    position={[0, 3.1, 0]}
                    // rotation={[0, Math.PI, 0]}
                  />
                  <mesh
                    visible={showHat}
                    name="hatAccessory"
                    geometry={nodes.hatAccessory.geometry}
                    material={hatAccessMaterial}
                    position={[0, 3.6, 0]}
                    // rotation={[0, Math.PI, 0]}
                  />
                </group>
              </Suspense>
            </Canvas>
          </CanvasContainer>
        </Col>
        <Col className="option" md={4}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <Helmet>
                <title>{product.name}</title>
              </Helmet>
              <h1>{product.name}</h1>
            </ListGroup.Item>
            <ListGroup.Item>
              <Rating
                rating={product.rating}
                slug={product.slug}
                numReviews={product.numReviews}
              />
            </ListGroup.Item>
            <ListGroup.Item>Price : ${product.price}</ListGroup.Item>

            <ListGroup.Item>
              Description :<p>{product.description}</p>
            </ListGroup.Item>
          </ListGroup>
          <Card>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Row>
                    <Col>Price :</Col>
                    <Col>
                      ${product.price + headExtra + bodyExtra + legsExtra}
                    </Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col>Status :</Col>
                    <Col>
                      {product.countInStock > 0 ? (
                        <Badge bg="success">Available</Badge>
                      ) : (
                        <Badge bg="danger">Unavailable</Badge>
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
                {product.countInStock > 0 && (
                  <ListGroup.Item>
                    <div className="d-grid">
                      <Button variant="primary" onClick={addToCartHandler}>
                        Add to Cart
                      </Button>
                    </div>
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
        <div className="d-flex flex-wrap align-items-center justify-content-start w-100 option">
          <ButtonsContainer>
            <button className="part-btn" onClick={() => open('head')}>
              <img
                onMouseEnter={(e) => {
                  hoverColor(e.target);
                }}
                onMouseLeave={(e) => {
                  hoverUncolor(e.target);
                }}
                src="/images/head.svg"
                alt="head of lego"
              />
            </button>

            <button className="part-btn" onClick={() => open('body')}>
              <img
                onMouseEnter={(e) => {
                  hoverColor(e.target);
                }}
                onMouseLeave={(e) => {
                  hoverUncolor(e.target);
                }}
                src="/images/torso.svg"
                alt="torso of lego"
              />
            </button>

            <button className="part-btn" onClick={() => open('hands')}>
              <img
                onMouseEnter={(e) => {
                  hoverColor(e.target);
                }}
                onMouseLeave={(e) => {
                  hoverUncolor(e.target);
                }}
                src="/images/hands.svg"
                alt="hands of lego"
              />
            </button>

            <button className="part-btn" onClick={() => open('legs')}>
              <img
                onMouseEnter={(e) => {
                  hoverColor(e.target);
                }}
                onMouseLeave={(e) => {
                  hoverUncolor(e.target);
                }}
                src="/images/legs.svg"
                alt="legs of lego"
              />
            </button>
          </ButtonsContainer>
          {!openedOptions ? (
            <> Choose a part of lego to edit</>
          ) : arrayOfOptions.part === 'hands' ? (
            <div className="d-flex">
              <Picker />
              <div className="d-flex flex-column align-content-center justify-content-start">
                <button
                  style={{
                    border: 'none',
                    backgroundColor: 'white',
                    marginTop: '15px'
                  }}
                  onClick={() => setOpenedOptions(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
                <button
                  style={{
                    border: 'none',
                    backgroundColor: 'white',
                    position: 'relative',
                    width: '45px',
                    height: '45px',
                    marginTop: '15px'
                  }}
                  onClick={() => setShowHat((prev) => !prev)}
                >
                  <img
                    className="hat-img"
                    src="/images/hatIcon.png"
                    alt="an icon of a hat"
                  />
                  {showHat && (
                    <img
                      className="forbidden-img"
                      src="/images/forbidden.png"
                      alt="forbidden icon"
                    />
                  )}
                </button>
              </div>
            </div>
          ) : (
            <Col className="my-3 d-flex flex-wrap">
              {arrayOfOptions.options.map((o, i) => (
                //o is gonna be an img src
                <Col md={2} key={i} className="d-flex flex-column">
                  <button
                    className="choise-btn"
                    onClick={() => changeTexutre(arrayOfOptions.part, o)}
                  >
                    <img
                      src={o.src}
                      style={{ width: '80px' }}
                      alt="choise for texture"
                    />
                  </button>
                  <span className="text-center">
                    <strong>+ {o.price} $</strong>
                  </span>
                </Col>
              ))}
              <div className="d-flex flex-column align-content-center justify-content-start">
                <button
                  style={{
                    border: 'none',
                    backgroundColor: 'white',
                    marginTop: '15px'
                  }}
                  onClick={() => setOpenedOptions(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
                <button
                  className="d-flex flex-column"
                  style={{
                    border: 'none',
                    backgroundColor: 'white',
                    position: 'relative',
                    width: '45px',
                    height: '45px',
                    marginTop: '15px'
                  }}
                  onClick={() => setShowHat((prev) => !prev)}
                >
                  <img
                    className="hat-img"
                    src="/images/hatIcon.png"
                    alt="an icon of a hat"
                  />
                  {showHat && (
                    <img
                      className="forbidden-img"
                      src="/images/forbidden.png"
                      alt="forbidden icon"
                    />
                  )}
                </button>
              </div>
            </Col>
          )}
        </div>
      </div>
      <div style={{ marginTop: '10rem', marginBottom: '2rem' }} id="reviews">
        <h2 ref={reviewsRef}>Reviews</h2>
        <div className="mb-3">
          {product.reviews.length === 0 && (
            <MessageBox>There is no review</MessageBox>
          )}
        </div>
        <ListGroup>
          {product.reviews.map((review) => (
            <ListGroup.Item key={review._id}>
              <strong>{review.name}</strong>
              <Rating rating={review.rating} caption=" "></Rating>
              <p>{review.createdAt.substring(0, 10)}</p>
              <p>{review.comment}</p>
            </ListGroup.Item>
          ))}
        </ListGroup>
        <div className="mb-3">
          {userInfo ? (
            haveCreatedReview ? (
              <MessageBox>
                {' '}
                You have allready created a review for this product
              </MessageBox>
            ) : (
              <Form onSubmit={submitHandler}>
                <h2>Write a customer review</h2>
                <Form.Group className="mb-3" controlId="rating">
                  <Form.Label>Rating</Form.Label>
                  <Form.Select
                    aria-label="Rating"
                    value={rating}
                    onChange={(e) => setRating(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="1">1- Poor</option>
                    <option value="2">2- Fair</option>
                    <option value="3">3- Good</option>
                    <option value="4">4- Very good</option>
                    <option value="5">5- Exelent</option>
                  </Form.Select>
                </Form.Group>
                <FloatingLabel
                  controlId="floatingTextarea"
                  label="Comment"
                  className="mb-3"
                >
                  <Form.Control
                    as="textarea"
                    placeholder="Leave a comment here"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </FloatingLabel>
                <div className="mb-3">
                  <Button disabled={loadingCreatingReview} type="submit">
                    submit
                  </Button>
                </div>
                {loadingCreatingReview && <LoadingBox />}
              </Form>
            )
          ) : (
            <MessageBox>
              Please{' '}
              <Link to={`/signin?redirect=/product/${product.slug}`}>
                Sign In
              </Link>{' '}
              to write a review
            </MessageBox>
          )}
        </div>
      </div>
    </Container>
  );
}

useGLTF.preload('/legoModel.glb');
